import React, { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchCustomerOrders } from '../lib/customerApi';
import './auth.css';

const VALID_VIEWS = new Set(['delivery', 'orders']);

const formatDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatAmount = (value) => `NGN ${Number(value || 0).toLocaleString()}`;

const getStatus = (order) => String(order.status || 'pending').toLowerCase();

const ProfilePage = () => {
  const {
    isAuthenticated,
    initializing,
    profile,
    profileLoading,
    saveProfile,
    getToken,
  } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const view = VALID_VIEWS.has(requestedView) ? requestedView : 'dashboard';
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');

  useEffect(() => {
    if (profile) {
      setForm({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        address: profile.address || '',
      });
    }
  }, [profile]);

  useEffect(() => {
    if (!isAuthenticated || view !== 'orders') return undefined;

    let cancelled = false;

    const loadOrders = async () => {
      setOrdersLoading(true);
      setOrdersError('');

      try {
        const token = await getToken();
        const rows = await fetchCustomerOrders(token);
        if (!cancelled) setOrders(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (!cancelled) setOrdersError(err.message || 'Unable to load your orders.');
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    };

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, [getToken, isAuthenticated, view]);

  if (initializing) {
    return <main className="profile-page"><p className="auth-subtitle">Loading your profile...</p></main>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/account?redirect=/profile" replace />;
  }

  const showDashboard = () => {
    setMessage('');
    setError('');
    setSearchParams({});
  };

  const showView = (nextView) => {
    setMessage('');
    setError('');
    setSearchParams({ view: nextView });
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      await saveProfile({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
      });
      setMessage('Delivery details updated successfully.');
    } catch (err) {
      setError(err.message || 'Unable to save delivery details.');
    } finally {
      setSaving(false);
    }
  };

  const renderDashboard = () => (
    <div className="profile-dashboard">
      <button type="button" className="profile-action-row" onClick={() => showView('delivery')}>
        <span>
          <strong>Update Delivery Details</strong>
          <small>Name, email, phone number, and address</small>
        </span>
        <span aria-hidden="true">-&gt;</span>
      </button>

      <button type="button" className="profile-action-row" onClick={() => showView('orders')}>
        <span>
          <strong>Order History</strong>
          <small>Review past orders and open tracking details</small>
        </span>
        <span aria-hidden="true">-&gt;</span>
      </button>
    </div>
  );

  const renderDeliveryDetails = () => (
    <>
      <div className="profile-screen-heading">
        <div>
          <h2>Delivery Details</h2>
          <p className="auth-subtitle">Keep your checkout contact information up to date.</p>
        </div>
        <button type="button" className="profile-back-btn" onClick={showDashboard}>
          Back
        </button>
      </div>

      {profileLoading && !profile ? (
        <p className="auth-subtitle">Loading profile...</p>
      ) : (
        <form className="auth-form" onSubmit={onSubmit} noValidate>
          <div className="co-field-row">
            <div className="auth-field">
              <label htmlFor="first_name" className="auth-label">First Name</label>
              <input id="first_name" name="first_name" className="auth-input" value={form.first_name} onChange={onChange} required />
            </div>
            <div className="auth-field">
              <label htmlFor="last_name" className="auth-label">Last Name</label>
              <input id="last_name" name="last_name" className="auth-input" value={form.last_name} onChange={onChange} required />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="email" className="auth-label">Email</label>
            <input id="email" name="email" type="email" className="auth-input" value={form.email} onChange={onChange} required readOnly />
          </div>

          <div className="auth-field">
            <label htmlFor="phone" className="auth-label">Phone Number</label>
            <input id="phone" name="phone" type="tel" className="auth-input" value={form.phone} onChange={onChange} required />
          </div>

          <div className="auth-field">
            <label htmlFor="address" className="auth-label">Address</label>
            <textarea id="address" name="address" className="auth-input" rows="3" value={form.address} onChange={onChange} required />
          </div>

          {message && <p className="auth-subtitle" role="status">{message}</p>}
          {error && <p className="auth-error" role="alert">{error}</p>}

          <button type="submit" className="auth-btn auth-btn--primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </form>
      )}
    </>
  );

  const renderOrderHistory = () => (
    <>
      <div className="profile-screen-heading">
        <div>
          <h2>Order History</h2>
          <p className="auth-subtitle">Track and review your customer orders.</p>
        </div>
        <button type="button" className="profile-back-btn" onClick={showDashboard}>
          Back
        </button>
      </div>

      {ordersLoading && <p className="auth-subtitle">Loading orders...</p>}
      {ordersError && <p className="auth-error" role="alert">{ordersError}</p>}

      {!ordersLoading && !ordersError && orders.length === 0 && (
        <p className="auth-subtitle">No orders yet.</p>
      )}

      {!ordersLoading && orders.length > 0 && (
        <ul className="orders-list">
          {orders.map((order) => {
            const status = getStatus(order);
            return (
              <li key={order.id} className="orders-item">
                <div className="orders-item-main">
                  <p><strong>Order ID</strong> {order.id}</p>
                  <p className="orders-item-meta">Date: {formatDate(order.created_at || order.requested_date)}</p>
                  <p className="orders-item-meta">Amount: {formatAmount(order.total ?? order.total_amount)}</p>
                </div>
                <div className="orders-item-actions">
                  <span className={`orders-status orders-status--${status}`}>{status}</span>
                  <Link to={`/order-status/${order.id}`} className="orders-tracking-link">View Tracking</Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );

  return (
    <main className="profile-page">
      <header className="profile-header">
        <p className="auth-eyebrow">Account</p>
        <h1 className="auth-title">Profile</h1>
        <p className="auth-subtitle">Manage your account details and order history.</p>
      </header>

      <div className="profile-card">
        {view === 'delivery' && renderDeliveryDetails()}
        {view === 'orders' && renderOrderHistory()}
        {view === 'dashboard' && renderDashboard()}
      </div>
    </main>
  );
};

export default ProfilePage;
